import * as Sentry from "@sentry/react"
import { useQueryClient } from "@tanstack/react-query"
import { AxiosError } from "axios"

import useOnboardContext from "./useOnboardContext"
import { QUERY_KEYS } from "../constants"
import { createChildren } from "../networking/authentication"
import { createFamily } from "../networking/authentication"
import { createUser } from "../networking/authentication"
import { loadFamilies } from "../networking/families/api"
import { useQuestionnairesQuery } from "../networking/queries"
import { getSubscriberInfo } from "../networking/subscriptions"
import { joinFamily } from "../networking/user"
import { loadUser, submitReferralCode } from "../networking/users/api"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../util/analytics"
import { submitParentQuestionnaires } from "../util/questionnaires"
import { submitChildQuestionnaires } from "../util/questionnaires"
import { localStorageItems } from "../util/storage"
import { sessionManager } from "../util/storage"
import { separateFamily } from "../util/util"

export const useAccountCreation = () => {
  const {
    isSubmittingData,
    setIsSubmittingData,
    parentData,
    isCoparent,
    coparentCode,
    setIsCreatingUser,
  } = useOnboardContext()
  const queryClient = useQueryClient()
  const { data: questionsResponse, status } = useQuestionnairesQuery()

  // Work the next screen doesn't need to render. Runs after the app has already
  // navigated, so it never blocks the onboarding → app transition. Failures here
  // are logged but intentionally swallowed — the account already exists.
  const uploadOnboardingBackgroundData = async (
    userData: Awaited<ReturnType<typeof loadUser>>,
    familiesData: Awaited<ReturnType<typeof loadFamilies>>,
    hasChildQuestionnaires: boolean
  ) => {
    try {
      // Submit referral code if applicable
      const referralCode = localStorage.getItem(
        localStorageItems.joonGiftCardReferralCode
      )
      if (referralCode) {
        trackAnalyticEvent(ANALYTIC_EVENTS.REFERRAL_GIFT_CARD_APPLIED, {
          uuid: referralCode,
        })
        await submitReferralCode(referralCode)
        localStorage.removeItem(localStorageItems.joonGiftCardReferralCode)
      }

      // Submit Questionnaires. Requires the questionnaire definitions — if they
      // haven't loaded we skip submission rather than blocking account creation.
      if (status === "loading" || status === "error" || !questionsResponse)
        return
      const { onboardingQuestionnaires } = questionsResponse
      const { children } = separateFamily(userData, familiesData)
      const questionnairePromises: Promise<void>[] = []
      if (hasChildQuestionnaires) {
        questionnairePromises.push(
          submitChildQuestionnaires(
            onboardingQuestionnaires,
            parentData,
            children
          )
        )
      }
      if (Object.keys(parentData.parentQuestionnaireMapping).length > 0) {
        questionnairePromises.push(
          submitParentQuestionnaires(
            onboardingQuestionnaires,
            parentData,
            userData.id
          )
        )
      }
      await Promise.all(questionnairePromises)

      trackAnalyticEvent("create-acc-questionnaires-submitted")
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setLevel("error")
        scope.setTag("function", "uploadOnboardingBackgroundData")
        Sentry.captureException(error)
      })
    }
  }

  const uploadOnboardingSecondaryData = async () => {
    localStorage.removeItem(localStorageItems.lastSeenPaywall)

    if (isSubmittingData) return

    const hasChildQuestionnaires =
      parentData.childrenData.length > 0 &&
      !(
        Array.isArray(parentData.childrenData[0].questionAnswerMapping) &&
        parentData.childrenData[0].questionAnswerMapping.length === 0
      )

    setIsSubmittingData(true)
    // Blocks the USER query (and therefore navigation) until we've created the
    // account and seeded the cache below.
    setIsCreatingUser(true)
    try {
      // Create User, Family, and Children
      await createUser(
        parentData.name,
        parentData.avatar as string,
        parentData.lastName || undefined
      )
      trackAnalyticEvent("create-acc-user-created")

      if (isCoparent) {
        await joinFamily(coparentCode, parentData.nickname)
        trackAnalyticEvent("create-acc-family-joined")
      } else {
        const family = await createFamily(parentData.nickname)
        Sentry.addBreadcrumb({
          category: "account",
          message: `Family created: ${family?.data?.id}`,
          level: "info",
        })
        const children = await createChildren(
          family.data.id,
          parentData.childrenData
        )
        Sentry.addBreadcrumb({
          category: "account",
          message: `Children created: ${JSON.stringify(children)}`,
          level: "info",
        })
        trackAnalyticEvent("create-acc-family-created")

        // Set marker to show freemium popup when verifying first Quest.
        // We only need to set new user flags for those that are creating a family.
        sessionManager.setNewUserLocalStorageFlags()
      }

      // Load the canonical user + family and seed the query cache directly. This
      // lets the app navigate immediately off fresh cached data instead of
      // waiting on (and retrying) a read-after-write refetch of the USER query.
      const [userData, familiesData] = await Promise.all([
        loadUser(),
        loadFamilies(),
      ])
      if (!familiesData || !userData)
        throw new Error(
          `Error loading data families:${familiesData} user:${userData}`
        )

      // Set paywall flag if applicable. Kept in the blocking phase so the paywall
      // decision (usePaywall) is ready by the time the next screen mounts.
      const subscriberInfo = await getSubscriberInfo(familiesData)
      if (!subscriberInfo.plan_is_active) {
        sessionManager.setLocalStorageFlag(localStorageItems.needsToSeePaywall)
        sessionManager.setLocalStorageFlag(
          localStorageItems.needsToSeePrePaywall
        )
      }

      queryClient.setQueryData([QUERY_KEYS.USER], userData)
      queryClient.setQueryData([QUERY_KEYS.FAMILY], familiesData)
      // Re-enable the USER query. The cache is now fresh, so this triggers
      // navigation without an extra network round-trip.
      setIsCreatingUser(false)

      // Fire-and-forget the rest so it doesn't block the transition.
      void uploadOnboardingBackgroundData(
        userData,
        familiesData,
        hasChildQuestionnaires
      )
    } catch (error) {
      setIsCreatingUser(false)
      Sentry.withScope((scope) => {
        scope.setLevel("error")
        scope.setTag("function", "uploadOnboardingSecondaryData")
        scope.setExtra("status", status)
        scope.setExtra("isSubmittingData", isSubmittingData)
        scope.setExtra("parentData", JSON.stringify(parentData))
        scope.setExtra("isCoparent", isCoparent)
        scope.setExtra("coparentCode", coparentCode)

        if (error instanceof AxiosError) {
          scope.setExtra("axiosError", {
            message: error.message,
            code: error.code,
            response: error.response
              ? {
                  data: error.response.data,
                  status: error.response.status,
                  headers: error.response.headers,
                }
              : null,
            request: error.request
              ? {
                  method: error.request.method,
                  url: error.request.url,
                  headers: error.request.headers,
                }
              : null,
          })
        } else {
          scope.setExtra("error", {
            message: (error as Error)?.message,
            stack: (error as Error)?.stack,
          })
        }

        Sentry.captureException(error)
      })

      // Instead of throwing, return an error object
      throw error
    } finally {
      setIsSubmittingData(false)
    }
  }
  return { uploadOnboardingSecondaryData }
}
